/**
 * 儲值帳本組件 - 前後台共用
 * @description 顯示儲值交易記錄，支援唯讀和管理模式
 */

import { useState, useEffect } from 'react';
import { supabase } from '../../../services/supabaseClient';

import { adminOrderDetailPath } from '../../../utils/orderRoutes';

interface PrepaidTransaction {
  id: string;
  account_id: string;
  transaction_uuid: string;
  transaction_type: 'deposit' | 'deduct' | 'refund' | 'bonus' | 'adjustment';
  real_amount: number;
  bonus_amount: number;
  balance_before: number;
  balance_after: number;
  reference_type: string | null;
  reference_id: string | null;
  description: string;
  performed_by: string | null;
  requires_invoice: boolean;
  invoice_number: string | null;
  created_at: string;
  performer?: {
    name: string;
  };
}

interface PrepaidAccount {
  id: string;
  customer_id: string;
  real_balance: number;
  bonus_balance: number;
  total_balance: number;
  total_deposited: number;
  total_bonus: number;
  total_spent: number;
  status: string;
}

interface PrepaidLedgerProps {
  /** 客戶 ID */
  customerId: string;
  
  /** 是否為管理模式（後台） */
  isAdminMode?: boolean;
  
  /** 是否顯示操作按鈕 */
  showActions?: boolean;
  
  /** 自訂標題 */
  title?: string;
}

export default function PrepaidLedger({ 
  customerId, 
  isAdminMode = false,
  showActions = false,
  title = '儲值帳本'
}: PrepaidLedgerProps) {
  const [account, setAccount] = useState<PrepaidAccount | null>(null);
  const [transactions, setTransactions] = useState<PrepaidTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTransaction, setSelectedTransaction] = useState<PrepaidTransaction | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  useEffect(() => {
    loadAccountAndTransactions();
  }, [customerId]);

  const loadAccountAndTransactions = async () => {
    try {
      setLoading(true);

      // 載入帳戶資訊
      const { data: accountData, error: accountError } = await supabase
        .from('prepaid_accounts')
        .select('*')
        .eq('customer_id', customerId)
        .maybeSingle();

      if (accountError) throw accountError;

      if (!accountData) {
        setAccount(null);
        setTransactions([]);
        setLoading(false);
        return;
      }

      setAccount(accountData);

      // 載入交易記錄（排除已作廢的記錄）
      const { data: transactionsData, error: transactionsError } = await supabase
        .from('prepaid_transactions')
        .select(`
          *,
          performer:performed_by (name)
        `)
        .eq('account_id', accountData.id)
        .or('is_voided.is.null,is_voided.eq.false')
        .order('created_at', { ascending: false });

      if (transactionsError) throw transactionsError;

      setTransactions(transactionsData || []);
    } catch (error) {
      console.error('載入儲值帳本失敗:', error);
    } finally {
      setLoading(false);
    }
  };

  const getTransactionTypeConfig = (type: string) => {
    const configs = {
      deposit: {
        label: '儲值',
        icon: 'ri-arrow-down-circle-line',
        color: 'text-blue-600',
        bgColor: 'bg-blue-50',
        amountColor: 'text-blue-600',
        sign: '+',
      },
      bonus: {
        label: '贈送',
        icon: 'ri-gift-line',
        color: 'text-teal-600',
        bgColor: 'bg-teal-50',
        amountColor: 'text-teal-600',
        sign: '+',
      },
      deduct: {
        label: '扣款',
        icon: 'ri-arrow-up-circle-line',
        color: 'text-red-600',
        bgColor: 'bg-red-50',
        amountColor: 'text-red-600',
        sign: '-',
      },
      refund: {
        label: '退款',
        icon: 'ri-refund-line',
        color: 'text-green-600',
        bgColor: 'bg-green-50',
        amountColor: 'text-green-600',
        sign: '+',
      },
      adjustment: {
        label: '調整',
        icon: 'ri-edit-line',
        color: 'text-amber-600',
        bgColor: 'bg-amber-50',
        amountColor: 'text-amber-600',
        sign: '±',
      },
    };
    return configs[type as keyof typeof configs] || configs.adjustment;
  };

  const formatDateTime = (dateStr: string) => {
    return new Date(dateStr).toLocaleString('zh-TW', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatAmount = (amount: number) => {
    return `NT$ ${Math.abs(amount).toLocaleString()}`;
  };

  const handleViewDetail = (transaction: PrepaidTransaction) => {
    setSelectedTransaction(transaction);
    setShowDetailModal(true);
  };

  const handleViewOrder = (orderId: string) => {
    const path = adminOrderDetailPath(orderId);
    if (path) {
      if (isAdminMode) {
        window.open(path, '_blank');
      } else {
        window.open(path.replace('/admin/orders/', '/account/orders/'), '_blank');
      }
    }
  };

  // 無帳戶狀態
  if (!loading && !account) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-12">
        <div className="text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="ri-wallet-3-line text-3xl text-gray-400"></i>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">尚未開通儲值帳戶</h3>
          <p className="text-gray-500">
            {isAdminMode ? '此客戶尚未開通儲值帳戶' : '您尚未開通儲值帳戶，請先申請儲值會員'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* 帳戶餘額卡片 */}
      {account && (
        <div className="bg-gradient-to-br from-teal-500 to-teal-600 rounded-2xl p-8 text-white shadow-lg">
          <div className="flex items-start justify-between mb-6">
            <div>
              <h3 className="text-lg font-medium opacity-90 mb-1">{title}</h3>
              <p className="text-sm opacity-75">帳戶餘額</p>
            </div>
            <div className={`px-3 py-1 rounded-full text-xs font-medium ${
              account.status === 'active' ? 'bg-white/20' : 'bg-red-500/50'
            }`}>
              {account.status === 'active' ? '正常' : account.status === 'frozen' ? '凍結' : '已關閉'}
            </div>
          </div>

          <div className="mb-6">
            <div className="text-4xl font-bold mb-2">
              NT$ {(account.real_balance + account.bonus_balance).toLocaleString()}
            </div>
            <div className="flex items-center gap-4 text-sm opacity-90">
              <div>
                實體金額：NT$ {account.real_balance.toLocaleString()}
              </div>
              <div>
                贈送金額：NT$ {account.bonus_balance.toLocaleString()}
              </div>
            </div>
          </div>

          {/* 餘額警示 */}
          {(account.real_balance + account.bonus_balance) < 3000 && (
            <div className="bg-red-500/20 border border-red-300/30 rounded-lg p-3 mb-4">
              <div className="flex items-start gap-2">
                <i className="ri-alert-line text-lg mt-0.5"></i>
                <div className="flex-1">
                  <p className="text-sm font-medium mb-1">餘額不足提醒</p>
                  <p className="text-xs opacity-90">
                    您的儲值餘額已低於 NT$ 3,000，建議儲值以確保租借順暢。
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-3 gap-4 pt-4 border-t border-white/20">
            <div>
              <p className="text-xs opacity-75 mb-1">累計儲值</p>
              <p className="text-lg font-semibold">NT$ {account.total_deposited.toLocaleString()}</p>
            </div>
            <div>
              <p className="text-xs opacity-75 mb-1">累計贈送</p>
              <p className="text-lg font-semibold">NT$ {account.total_bonus.toLocaleString()}</p>
            </div>
            <div>
              <p className="text-xs opacity-75 mb-1">累計消費</p>
              <p className="text-lg font-semibold">NT$ {account.total_spent.toLocaleString()}</p>
            </div>
          </div>
        </div>
      )}

      {/* 交易記錄列表 */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
          <h3 className="font-semibold text-gray-900 flex items-center gap-2">
            <i className="ri-file-list-3-line text-teal-500"></i>
            交易記錄
          </h3>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="w-10 h-10 border-3 border-gray-200 border-t-teal-500 rounded-full animate-spin mx-auto mb-3"></div>
            <p className="text-gray-500 text-sm">載入中...</p>
          </div>
        ) : transactions.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-inbox-line text-3xl text-gray-400"></i>
            </div>
            <p className="text-gray-500">尚無交易記錄</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {transactions.map((transaction) => {
              const config = getTransactionTypeConfig(transaction.transaction_type);
              const totalAmount = transaction.real_amount + transaction.bonus_amount;
              
              return (
                <div key={transaction.id} className="px-6 py-4 hover:bg-gray-50 transition-colors">
                  <div className="flex items-start gap-4">
                    {/* 圖示 */}
                    <div className={`w-10 h-10 flex items-center justify-center ${config.bgColor} rounded-lg flex-shrink-0`}>
                      <i className={`${config.icon} text-lg ${config.color}`}></i>
                    </div>

                    {/* 內容 */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-4 mb-2">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`px-2 py-0.5 ${config.bgColor} ${config.color} text-xs font-medium rounded whitespace-nowrap`}>
                              {config.label}
                            </span>
                            {transaction.requires_invoice && (
                              <span className="px-2 py-0.5 bg-blue-50 text-blue-600 text-xs font-medium rounded whitespace-nowrap">
                                需開發票
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-900 font-medium mb-1">
                            {transaction.description}
                          </p>
                          <div className="flex items-center gap-3 text-xs text-gray-500">
                            <span>{formatDateTime(transaction.created_at)}</span>
                            {isAdminMode && transaction.performer && (
                              <span>操作人：{transaction.performer.name}</span>
                            )}
                          </div>
                        </div>

                        {/* 金額 */}
                        <div className="text-right flex-shrink-0">
                          <div className={`text-lg font-bold ${config.amountColor} mb-1`}>
                            {config.sign} {formatAmount(totalAmount)}
                          </div>
                          {transaction.real_amount > 0 && transaction.bonus_amount > 0 && (
                            <div className="text-xs text-gray-500 space-y-0.5">
                              <div>實體：{formatAmount(transaction.real_amount)}</div>
                              <div>贈送：{formatAmount(transaction.bonus_amount)}</div>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* 餘額資訊 */}
                      <div className="flex items-center gap-4 text-xs text-gray-500 pt-2 border-t border-gray-100">
                        <span>餘額：{formatAmount(transaction.balance_before)} → {formatAmount(transaction.balance_after)}</span>
                        
                        {/* 操作按鈕 */}
                        <div className="flex items-center gap-2 ml-auto">
                          {transaction.reference_type === 'order' && transaction.reference_id && (
                            <button
                              onClick={() => handleViewOrder(transaction.reference_id!)}
                              className="text-teal-600 hover:text-teal-700 font-medium whitespace-nowrap cursor-pointer"
                            >
                              查看訂單 <i className="ri-arrow-right-line"></i>
                            </button>
                          )}
                          <button
                            onClick={() => handleViewDetail(transaction)}
                            className="text-gray-600 hover:text-gray-700 font-medium whitespace-nowrap cursor-pointer"
                          >
                            詳情 <i className="ri-arrow-right-line"></i>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* 交易詳情彈窗 */}
      {showDetailModal && selectedTransaction && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-start justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900">交易詳情</h3>
              <button
                onClick={() => setShowDetailModal(false)}
                className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            <div className="space-y-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-gray-500 mb-1">交易類型</p>
                    <p className="text-sm font-medium text-gray-900">
                      {getTransactionTypeConfig(selectedTransaction.transaction_type).label}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">交易時間</p>
                    <p className="text-sm font-medium text-gray-900">
                      {formatDateTime(selectedTransaction.created_at)}
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <p className="text-xs text-gray-500 mb-2">交易說明</p>
                <p className="text-sm text-gray-900">{selectedTransaction.description}</p>
              </div>

              <div className="bg-teal-50 rounded-lg p-4">
                <p className="text-xs text-teal-700 font-medium mb-3">金額明細</p>
                <div className="space-y-2">
                  {selectedTransaction.real_amount > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-700">實體金額</span>
                      <span className="font-semibold text-gray-900">
                        {formatAmount(selectedTransaction.real_amount)}
                      </span>
                    </div>
                  )}
                  {selectedTransaction.bonus_amount > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-700">贈送金額</span>
                      <span className="font-semibold text-teal-600">
                        {formatAmount(selectedTransaction.bonus_amount)}
                      </span>
                    </div>
                  )}
                  <div className="pt-2 border-t border-teal-200 flex justify-between">
                    <span className="text-sm font-medium text-gray-900">總計</span>
                    <span className="text-lg font-bold text-teal-600">
                      {formatAmount(selectedTransaction.real_amount + selectedTransaction.bonus_amount)}
                    </span>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-xs text-gray-500 mb-3">餘額變動</p>
                <div className="flex items-center justify-between">
                  <div className="text-center">
                    <p className="text-xs text-gray-500 mb-1">交易前</p>
                    <p className="text-lg font-semibold text-gray-900">
                      {formatAmount(selectedTransaction.balance_before)}
                    </p>
                  </div>
                  <i className="ri-arrow-right-line text-2xl text-gray-400"></i>
                  <div className="text-center">
                    <p className="text-xs text-gray-500 mb-1">交易後</p>
                    <p className="text-lg font-semibold text-teal-600">
                      {formatAmount(selectedTransaction.balance_after)}
                    </p>
                  </div>
                </div>
              </div>

              {selectedTransaction.invoice_number && (
                <div>
                  <p className="text-xs text-gray-500 mb-1">發票號碼</p>
                  <p className="text-sm font-medium text-gray-900">{selectedTransaction.invoice_number}</p>
                </div>
              )}

              {isAdminMode && (
                <>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">交易 UUID</p>
                    <p className="text-xs font-mono text-gray-600 break-all">
                      {selectedTransaction.transaction_uuid}
                    </p>
                  </div>
                  {selectedTransaction.performer && (
                    <div>
                      <p className="text-xs text-gray-500 mb-1">操作人員</p>
                      <p className="text-sm font-medium text-gray-900">
                        {selectedTransaction.performer.name}
                      </p>
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="mt-6 pt-6 border-t border-gray-200">
              <button
                onClick={() => setShowDetailModal(false)}
                className="w-full px-4 py-2.5 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors font-medium cursor-pointer whitespace-nowrap"
              >
                關閉
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
